import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Loader2, CreditCard, TrendingUp, Users, Filter } from 'lucide-react';
import { adminAPI } from '../lib/api';

const fmtCLP = (n) => `$${(n || 0).toLocaleString('es-CL')}`;

const STATUS_BADGE = {
    approved: { text: 'Aprobado', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    pending: { text: 'Pendiente', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
    rejected: { text: 'Rechazado', cls: 'bg-red-100 text-red-800 border-red-200' },
    refunded: { text: 'Reembolsado', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
};

const PLAN_BADGE = {
    plataforma: { text: 'Plataforma', cls: 'bg-sky-100 text-sky-800 border-sky-200' },
    premium: { text: 'Premium', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
    upgrade: { text: 'Upgrade', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
};

function fmtDate(iso) {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return iso; }
}

export default function PagosPanel() {
    const [pagos, setPagos] = useState([]);
    const [planes, setPlanes] = useState([]);
    const [loadingPagos, setLoadingPagos] = useState(true);
    const [loadingPlanes, setLoadingPlanes] = useState(true);
    const [statusFilter, setStatusFilter] = useState('todos');
    const [planFilter, setPlanFilter] = useState('todos');
    const [modalidadFilter, setModalidadFilter] = useState('todas');
    const [q, setQ] = useState('');
    const [resumen, setResumen] = useState(null);

    useEffect(() => { load(); }, [statusFilter, planFilter, modalidadFilter]);

    const load = async () => {
        setLoadingPagos(true); setLoadingPlanes(true);
        try {
            const [rp, rpl, rr] = await Promise.all([
                adminAPI.getPagos({ status_filter: statusFilter, plan_filter: planFilter, limit: 200 }),
                adminAPI.getPlanes({ modalidad_filter: modalidadFilter, limit: 300 }),
                adminAPI.getPagosResumen().catch(() => ({ data: null })),
            ]);
            setPagos(rp.data.pagos || []);
            setPlanes(rpl.data.planes || []);
            setResumen(rr.data || null);
        } catch (e) {
            console.error('PagosPanel load error', e);
        } finally {
            setLoadingPagos(false); setLoadingPlanes(false);
        }
    };

    const filteredPlanes = planes.filter((p) => {
        if (!q) return true;
        const s = `${p.email || ''} ${p.nombre || ''}`.toLowerCase();
        return s.includes(q.toLowerCase());
    });

    const kpis = {
        total_pagos: pagos.length,
        aprobados: pagos.filter((p) => p.mp_status === 'approved').length,
        monto_total: pagos.filter((p) => p.mp_status === 'approved').reduce((s, p) => s + (p.monto || 0), 0),
        con_plan: planes.filter((p) => p.plan_activo).length,
    };

    return (
        <div className="space-y-6" data-testid="pagos-panel">
            {/* Header */}
            <div>
                <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-indigo-600" />
                    Gestión de Planes y Pagos
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                    Panel de consulta de pagos automáticos (Mercado Pago) y planes activos por usuario. Sin registro manual.
                </p>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card>
                    <CardContent className="p-4">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" /> Ingresos totales
                        </div>
                        <div className="font-mono text-2xl font-extrabold text-emerald-700 mt-1" data-testid="kpi-ingresos">
                            {fmtCLP(kpis.monto_total)}
                        </div>
                        <div className="text-[11px] text-slate-500">Pagos aprobados</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Aprobados</div>
                        <div className="font-mono text-2xl font-extrabold text-slate-900 mt-1">{kpis.aprobados}</div>
                        <div className="text-[11px] text-slate-500">De {kpis.total_pagos} transacciones</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1">
                            <Users className="h-3 w-3" /> Docentes con plan
                        </div>
                        <div className="font-mono text-2xl font-extrabold text-indigo-700 mt-1">{kpis.con_plan}</div>
                        <div className="text-[11px] text-slate-500">De {planes.length} registrados</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Comunidad Premium</div>
                        <div className="font-mono text-2xl font-extrabold text-amber-700 mt-1">
                            {planes.filter((p) => p.acceso_comunidad_premium).length}
                        </div>
                        <div className="text-[11px] text-slate-500">Con acceso WhatsApp</div>
                    </CardContent>
                </Card>
            </div>

            {/* Historial de Pagos */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                            <CardTitle className="text-base">Historial de Pagos Mercado Pago</CardTitle>
                            <CardDescription className="text-xs">
                                Todos los pagos son registrados automáticamente al confirmar Mercado Pago.
                            </CardDescription>
                        </div>
                        <div className="flex gap-2 items-center">
                            <Filter className="h-4 w-4 text-slate-400" />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="text-xs border border-slate-200 rounded px-2 py-1"
                                data-testid="filter-status"
                            >
                                <option value="todos">Todos los estados</option>
                                <option value="approved">Aprobados</option>
                                <option value="pending">Pendientes</option>
                                <option value="rejected">Rechazados</option>
                                <option value="refunded">Reembolsados</option>
                            </select>
                            <select
                                value={planFilter}
                                onChange={(e) => setPlanFilter(e.target.value)}
                                className="text-xs border border-slate-200 rounded px-2 py-1"
                                data-testid="filter-plan"
                            >
                                <option value="todos">Todos los planes</option>
                                <option value="plataforma">Plataforma</option>
                                <option value="premium">Premium</option>
                                <option value="upgrade">Upgrade</option>
                            </select>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {loadingPagos ? (
                        <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
                            <Loader2 className="h-4 w-4 animate-spin" /> Cargando pagos…
                        </div>
                    ) : pagos.length === 0 ? (
                        <div className="text-center py-8" data-testid="pagos-empty">
                            <CreditCard className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                            <p className="text-sm text-slate-600 font-medium">Aún no hay pagos registrados</p>
                            <p className="text-xs text-slate-400 mt-1">
                                Los pagos se registrarán automáticamente cuando Mercado Pago confirme la primera transacción.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs" data-testid="pagos-table">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-500">Fecha</th>
                                        <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-500">Docente</th>
                                        <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-500">Plan</th>
                                        <th className="px-3 py-2 text-right font-bold uppercase tracking-wider text-slate-500">Monto</th>
                                        <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-500">Estado</th>
                                        <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-500">MP ID</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagos.map((p) => {
                                        const st = STATUS_BADGE[p.mp_status] || { text: p.mp_status || '—', cls: 'bg-slate-100 text-slate-700' };
                                        const pl = PLAN_BADGE[p.plan_tipo] || { text: p.plan_tipo || '—', cls: 'bg-slate-100 text-slate-700' };
                                        return (
                                            <tr key={p.id || p.mp_payment_id} className="border-t border-slate-100">
                                                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmtDate(p.created_at)}</td>
                                                <td className="px-3 py-2">
                                                    <div className="font-medium text-slate-900">{p.nombre || '—'}</div>
                                                    <div className="text-[10px] text-slate-500">{p.email}</div>
                                                </td>
                                                <td className="px-3 py-2"><Badge className={pl.cls}>{pl.text}</Badge></td>
                                                <td className="px-3 py-2 text-right font-mono font-bold text-slate-900">{fmtCLP(p.monto)}</td>
                                                <td className="px-3 py-2"><Badge className={st.cls}>{st.text}</Badge></td>
                                                <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{p.mp_payment_id || '—'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Tabla de Planes por Usuario */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                            <CardTitle className="text-base">Planes por Usuario</CardTitle>
                            <CardDescription className="text-xs">
                                Estado actual de cada docente. Incluye días restantes de soporte WhatsApp individual y modalidad.
                            </CardDescription>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <select
                                value={modalidadFilter}
                                onChange={(e) => setModalidadFilter(e.target.value)}
                                className="text-xs border border-slate-200 rounded px-2 py-1"
                                data-testid="filter-modalidad"
                            >
                                <option value="todas">Todas las modalidades</option>
                                <option value="Educación Básica">Ed. Básica</option>
                                <option value="Educación Media">Ed. Media</option>
                                <option value="Diferencial">Diferencial</option>
                                <option value="parvularia">Ed. Parvularia</option>
                                <option value="sin_definir">Sin definir</option>
                            </select>
                            <Input
                                placeholder="Buscar por nombre o email…"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                className="max-w-xs h-8 text-xs"
                                data-testid="filter-planes-q"
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {loadingPlanes ? (
                        <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
                            <Loader2 className="h-4 w-4 animate-spin" /> Cargando planes…
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs" data-testid="planes-table">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-500">Docente</th>
                                        <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-500">Modalidad</th>
                                        <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-500">Estado</th>
                                        <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-500">Plan</th>
                                        <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-500">WA días</th>
                                        <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-500">Comunidad</th>
                                        <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-500">Registro</th>
                                        <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-500">Último Pago</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredPlanes.map((p) => {
                                        const pl = p.plan_tipo ? (PLAN_BADGE[p.plan_tipo] || { text: p.plan_tipo, cls: 'bg-slate-100 text-slate-700' }) : null;
                                        const baseModoLabel = {
                                            plan: '✅ Activo', trial: '🟢 Trial libre',
                                            freemium_nuevo: '⚡ Freemium', legacy_bloqueado: '🔒 Bloqueado',
                                        }[p.freemium_modo] || p.freemium_modo;
                                        // Autorización individual (access_grants): prevalece visualmente
                                        // sobre "Bloqueado"/"Freemium" mientras esté vigente.
                                        let modoLabel = baseModoLabel;
                                        if (p.access_grant_active) {
                                            let fechaCorta = '';
                                            try {
                                                const d = new Date(p.access_grant_end_iso);
                                                fechaCorta = d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
                                            } catch { /* noop */ }
                                            modoLabel = `🟢 Acceso especial${fechaCorta ? ` hasta ${fechaCorta}` : ''}`;
                                        }
                                        return (
                                            <tr key={p.user_id} className="border-t border-slate-100">
                                                <td className="px-3 py-2">
                                                    <div className="font-medium text-slate-900">{p.nombre || '—'}</div>
                                                    <div className="text-[10px] text-slate-500">{p.email}</div>
                                                </td>
                                                <td className="px-3 py-2">
                                                    {p.modalidad === 'parvularia' ? (
                                                        <Badge className="bg-pink-100 text-pink-800 border border-pink-200">Parvularia</Badge>
                                                    ) : p.modalidad && p.modalidad !== 'sin_definir' ? (
                                                        <span className="text-[10px] text-slate-700">{p.modalidad}</span>
                                                    ) : (
                                                        <span className="text-[10px] text-slate-400">—</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{modoLabel}</td>
                                                <td className="px-3 py-2">
                                                    {pl ? <Badge className={pl.cls}>{pl.text}</Badge> : <span className="text-slate-400">—</span>}
                                                </td>
                                                <td className="px-3 py-2 text-center font-mono text-slate-700">
                                                    {p.wa_individual_dias_restantes === null ? '∞' : p.wa_individual_dias_restantes}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {p.acceso_comunidad_premium ? (
                                                        <span className="text-emerald-700 font-bold">✓</span>
                                                    ) : (
                                                        <span className="text-slate-300">—</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-[10px] text-slate-500 whitespace-nowrap">{fmtDate(p.created_at)}</td>
                                                <td className="px-3 py-2 text-[10px] text-slate-500 whitespace-nowrap">
                                                    {p.ultimo_pago_at ? `${fmtDate(p.ultimo_pago_at)} · ${fmtCLP(p.ultimo_pago_monto)}` : '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {filteredPlanes.length === 0 && (
                                <div className="text-center py-6 text-xs text-slate-500">Sin resultados para la búsqueda.</div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
